import './auth-modal.scss';

import { Alert } from 'antd';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { apiConnector } from '@/api/api.connector';
import { ROUTES, UZ_PHONE_MASK } from '@/shared/constants';
import useAppNavigate from '@/shared/hooks/useAppNavigation';
import { Button, Input, MaskedInput, Modal } from '@/shared/ui';
import { setIsAuth } from '@/store/auth.slice';
import { useAppDispatch, useAppSelector } from '@/store/store';

interface LoginFormValues {
  pinfl: string;
  phone: string;
}

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { navigate } = useAppNavigate();
  const dealerGroup = useAppSelector((state) => state.app.dealerGroup);

  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const methods = useForm<LoginFormValues>({
    mode: 'all',
    delayError: 200
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = methods;

  useEffect(() => {
    if (open) {
      reset({
        pinfl: '',
        phone: ''
      });
      setLoginError('');
    }
  }, [open, reset]);

  const onSubmit = async (values: LoginFormValues) => {
    setIsLoading(true);
    setLoginError('');

    try {
      const formattedPhone = values.phone.replace(/\D/g, '');
      const response = await apiConnector.clientLogin(
        values.pinfl,
        formattedPhone,
        dealerGroup?.uuid || ''
      );

      if (response.accessToken) {
        dispatch(setIsAuth(true));
        navigate(ROUTES.myOrders);
        onClose();
      } else {
        throw new Error(t('invalid_credentials'));
      }
    } catch (error: unknown) {
      console.error('Ошибка входа:', error);
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : t('auth_error');
      setLoginError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setLoginError('');
    onClose();
  };

  return (
    <Modal
      centered={true}
      className="auth-modal"
      title={t('login_title')}
      open={open}
      onClose={handleClose}
      maskClosable={true}
      footer={<></>}
    >
      <div className="auth-modal__content">
        <form className="auth-modal__form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="pinfl"
            rules={{
              required: t('pinfl_required'),
              minLength: {
                value: 14,
                message: t('pinfl_length')
              },
              pattern: {
                value: /^\d{14}$/,
                message: t('pinfl_digits_only')
              }
            }}
            render={({ field }) => (
              <Input
                {...field}
                inputMode="numeric"
                label={t('pinfl')}
                value={field.value ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.match(/\D/) || value.length > 14) return;
                  field.onChange(value);
                }}
                error={errors.pinfl?.message}
                isRequired
                style={{ fontFamily: 'monospace' }}
              />
            )}
          />

          <Controller
            control={control}
            name="phone"
            rules={{
              required: t('please_enter_data'),
              validate: (value) => {
                if (!value.match(/\+\d\d\d \(\d\d\) \d\d\d-\d\d-\d\d/))
                  return t('invalid_phone');
              }
            }}
            render={({ field }) => (
              <MaskedInput
                {...field}
                type="tel"
                label={t('phone_number')}
                value={field.value ?? ''}
                error={errors.phone?.message}
                mask={UZ_PHONE_MASK}
                unmask={false}
                isRequired
              />
            )}
          />

          {loginError && (
            <Alert
              message={loginError}
              type="error"
              showIcon
              closable
              onClose={() => setLoginError('')}
              className="auth-modal__alert"
            />
          )}

          <Button
            type="submit"
            className="auth-modal__submit"
            isLoading={isLoading}
            fullWidth
            variant="filled-black"
          >
            {t('login')}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default AuthModal;
